import { useMemo, useState } from "react";
import { styled } from "styled-components";

import { useGetCustomMapsInfiniteQuery } from "api/topicsApi.slice";
import { UIIconButton } from "common/components";
import { breakpoints } from "common/styles";
import { useDispatch, useSelector } from "common/utilities/react-redux";
import { useGo } from "common/utilities/useGo";
import { useTrack } from "common/utilities/useTrack";
import { useTranslate } from "common/utilities/useTranslate";
import { CreateLandingTitle } from "domains/create/landing/CreateLandingTitle";
import { UpgradeAccountForCreateCallToAction } from "domains/create/landing/UpgradeAccountForCreateCallToAction";
import { enterAIGeneratedMap } from "domains/createMapDetail/actions";
import { activateModal } from "domains/modal/actions";
import {
  getCanAccessGenerativeMaps,
  getCreateMapLimit,
  getIsCreateMapRestricted,
} from "entities/permissions/selectors";

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 1500px;
  padding: 5rem 3rem 4rem;
  box-sizing: border-box;
`;

const StyledButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  @media ${breakpoints.medium} {
    flex-direction: row;
  }
`;

export const CreateNewMapSection = () => {
  const dispatch = useDispatch();
  const go = useGo();
  const { data: paginatedCustomMaps, isLoading: areCustomMapsLoading } = useGetCustomMapsInfiniteQuery(undefined);

  const customMaps = useMemo(() => paginatedCustomMaps?.pages.flat(), [paginatedCustomMaps?.pages]);

  const canAccessGenerativeMaps = useSelector(getCanAccessGenerativeMaps);
  const mapLimit = useSelector(getCreateMapLimit);
  const isCreateMapRestricted = useSelector(getIsCreateMapRestricted);
  const [isCallToActionVisible, setCallToActionVisible] = useState<boolean>(false);
  const track = useTrack();
  const translate = useTranslate();

  const canCreate =
    !isCreateMapRestricted ||
    (!areCustomMapsLoading && (mapLimit === null || !customMaps || customMaps.length < mapLimit));

  const handleCreateMapClick = () => {
    if (canCreate) {
      go("/create/new");
    } else {
      dispatch(activateModal({ id: "CreateMapUpgrade" }));
    }
  };

  const handleCreateMapWithAssistantClick = () => {
    const trackChannel = canCreate ? "Wizard" : "Call to action";
    track({ Channel: trackChannel, name: "Open Map Assistant", page: "Create landing" });
    if (!canCreate) {
      dispatch(activateModal({ id: "CreateMapUpgrade" }));
    } else if (!canAccessGenerativeMaps) {
      setCallToActionVisible(true);
    } else {
      dispatch(enterAIGeneratedMap());
      go("/create/new");
    }
  };

  return (
    <StyledContainer>
      <CreateLandingTitle />
      <StyledButtonContainer>
        <UIIconButton
          data-test-id="create-a-new-custom-map-button"
          icon="rightArrow"
          isLoading={!customMaps?.length && areCustomMapsLoading}
          onClick={handleCreateMapClick}
          variant="filled"
        >
          {translate("app.create.landing_banner.button")}
        </UIIconButton>
        <UIIconButton
          data-test-id="create-a-new-custom-map-with-assistant-button"
          icon="magicWand"
          isLoading={!customMaps?.length && areCustomMapsLoading}
          onClick={handleCreateMapWithAssistantClick}
          variant="filled"
        >
          {translate("app.create.landing_banner.create_with_ai_button")}
        </UIIconButton>
      </StyledButtonContainer>
      <UpgradeAccountForCreateCallToAction
        isCallToActionVisible={isCallToActionVisible}
        setCallToActionVisible={setCallToActionVisible}
        type="digitalMemberCTA"
      />
    </StyledContainer>
  );
};
